import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ArrowLeft, Monitor, Smartphone, Tablet } from "lucide-react";
import { PREVIEW_SIZES, type PreviewSize } from "../lib/previewSize";
import { PreviewFrame } from "./PreviewFrame";

const ICONS = { desktop: Monitor, tablet: Tablet, phone: Smartphone } as const;

interface PreviewViewProps {
  /** The draft's preview origin, as the editor shows it. */
  root: string;
  /** The page to open on. */
  path: string;
  /** The site's home page, for a link that leaves the website. */
  home: string;
  title: string;
  startSize: PreviewSize;
  /** Back to the editor, on the page the preview ended on. */
  onClose: (path: string) => void;
}

/**
 * The site as visitors see it, at a desktop, tablet or phone width, over the whole screen. Nothing on
 * the page can be edited here: links work like on the live site, and one button goes back to editing.
 */
export function PreviewView({ root, path, home, title, startSize, onClose }: PreviewViewProps) {
  const [size, setSize] = useState<PreviewSize>(startSize);
  const [requested, setRequested] = useState(path);
  const [navigation, setNavigation] = useState(0);
  const shown = useRef(path);
  const back = useRef<HTMLButtonElement>(null);
  const close = () => onClose(shown.current);

  useEffect(() => {
    back.current?.focus();
  }, []);
  // Escape goes back to editing; the latest onClose is read when it's pressed.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const escape = (event: globalThis.KeyboardEvent) => { if (event.key === "Escape") closeRef.current(shown.current); };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, []);

  const index = PREVIEW_SIZES.findIndex((option) => option.size === size);
  const byArrow = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = PREVIEW_SIZES[(index + step + PREVIEW_SIZES.length) % PREVIEW_SIZES.length]!.size;
    setSize(next);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-size="${next}"]`)?.focus();
  };

  return (
    <div className="bw-preview-view" role="dialog" aria-modal="true" aria-label="Preview of your website">
      <header className="bw-preview-bar">
        <button ref={back} type="button" className="bw-btn bw-btn--quiet bw-preview-back" onClick={close}>
          <ArrowLeft size={18} aria-hidden="true" />
          <span>Back to editing</span>
        </button>
        <div className="bw-preview-sizes" role="radiogroup" aria-label="Screen size" onKeyDown={byArrow}>
          {PREVIEW_SIZES.map(({ size: value, label }) => {
            const Icon = ICONS[value];
            return (
              <button key={value} type="button" role="radio" data-size={value} aria-checked={size === value} aria-label={label} title={label} tabIndex={size === value ? 0 : -1} onClick={() => setSize(value)}>
                <Icon size={18} aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <span className="bw-preview-label">Preview</span>
      </header>
      <PreviewFrame
        root={root}
        path={requested}
        navigation={navigation}
        title={title}
        size={size}
        onPathChange={(next) => { shown.current = next; }}
        onReturnHome={() => {
          setRequested(home);
          setNavigation((now) => now + 1);
        }}
      />
    </div>
  );
}
